import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import apiClient, { getErrorMessage } from '../../utils/api';
import { Employee, EmployeeType } from '../../types/api';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, CheckCircleIcon, XCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface EmployeeFormData {
  employee_code: string;
  name: string;
  employee_type: EmployeeType;
  phone?: string;
  monthly_salary?: number;
  daily_wage?: number;
  joining_date: string;
  business_type: 'fish' | 'goat';
}

// Dynamic schema that validates based on employee type
const createEmployeeSchema = () => {
  return yup.object({
    employee_code: yup.string().required('Employee code is required').min(2, 'Code must be at least 2 characters'),
    name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
    employee_type: yup.string().oneOf(['manager', 'regular_farm_labour', 'daily_wage_labour']).required('Employee type is required'),
    phone: yup.string().optional(),
    monthly_salary: yup.number()
      .transform((value, originalValue) => {
        // Convert empty string or NaN to undefined
        if (originalValue === '' || originalValue === null || originalValue === undefined || isNaN(value)) {
          return undefined;
        }
        return value;
      })
      .when('employee_type', {
        is: (val: EmployeeType) => val === 'manager' || val === 'regular_farm_labour',
        then: (schema) => schema.required('Monthly salary is required').typeError('Monthly salary must be a valid number').positive('Salary must be greater than zero'),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    daily_wage: yup.number()
      .transform((value, originalValue) => {
        // Convert empty string or NaN to undefined
        if (originalValue === '' || originalValue === null || originalValue === undefined || isNaN(value)) {
          return undefined;
        }
        return value;
      })
      .when('employee_type', {
        is: 'daily_wage_labour',
        then: (schema) => schema.required('Daily wage is required').typeError('Daily wage must be a valid number').positive('Daily wage must be greater than zero'),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    joining_date: yup.string().required('Joining date is required'),
    business_type: yup.string().oneOf(['fish', 'goat']).required('Business type is required'),
  });
};

const EmployeeManagement: React.FC = () => {
  const { selectedLocation } = useLocationContext();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  const form = useForm<EmployeeFormData>({
    resolver: yupResolver(createEmployeeSchema()) as any,
    defaultValues: {
      employee_code: '',
      name: '',
      employee_type: 'daily_wage_labour',
      phone: '',
      monthly_salary: undefined,
      daily_wage: undefined,
      joining_date: new Date().toISOString().split('T')[0],
      business_type: businessType,
    },
  });

  const employeeType = form.watch('employee_type');

  useEffect(() => {
    if (selectedLocation) {
      fetchEmployees();
    }
  }, [selectedLocation, businessType]);

  const fetchEmployees = async () => {
    if (!selectedLocation) return;
    setLoading(true);
    try {
      // Fetch all employees regardless of business type
      const response = await apiClient.get<Employee[]>(`/employees?location_id=${selectedLocation.id}`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateEmployeeCode = async (empType?: EmployeeType) => {
    if (!selectedLocation || editingEmployee) return;
    const employeeType = empType || form.getValues('employee_type');
    if (!employeeType) return;
    
    setGeneratingCode(true);
    try {
      const response = await apiClient.get<{ employee_code: string }>(`/employees/next-code?location_id=${selectedLocation.id}&employee_type=${employeeType}`);
      form.setValue('employee_code', response.data.employee_code);
    } catch (error) {
      console.error('Error generating employee code:', error);
    } finally {
      setGeneratingCode(false);
    }
  };

  useEffect(() => {
    if (editingEmployee) {
      form.reset({
        employee_code: editingEmployee.employee_code,
        name: editingEmployee.name,
        employee_type: editingEmployee.employee_type,
        phone: editingEmployee.phone || '',
        monthly_salary: editingEmployee.monthly_salary,
        daily_wage: editingEmployee.daily_wage,
        joining_date: editingEmployee.joining_date.split('T')[0],
      });
    } else {
      form.reset({
        employee_code: '',
        name: '',
        employee_type: 'daily_wage_labour',
        phone: '',
        monthly_salary: undefined,
        daily_wage: undefined,
        joining_date: new Date().toISOString().split('T')[0],
        business_type: businessType,
      });
    }
  }, [editingEmployee, form]);

  // Generate employee code when form opens for new employee or when employee type changes
  useEffect(() => {
    if (showForm && !editingEmployee && selectedLocation && employeeType) {
      generateEmployeeCode(employeeType);
    }
  }, [showForm, editingEmployee, selectedLocation, employeeType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Reset salary/wage fields when employee type changes
    if (employeeType === 'daily_wage_labour') {
      form.setValue('monthly_salary', undefined);
      form.clearErrors('monthly_salary');
    } else {
      form.setValue('daily_wage', undefined);
      form.clearErrors('daily_wage');
    }
  }, [employeeType, form]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
    try {
      await apiClient.delete(`/employees/${id}`);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      alert('Failed to deactivate employee');
    }
  };

  const handleToggleActive = async (employee: Employee) => {
    const newStatus = !employee.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this employee?`)) return;
    
    try {
      await apiClient.put(`/employees/${employee.id}`, {
        is_active: newStatus,
      });
      fetchEmployees();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, `Failed to ${action} employee`);
      alert(errorMessage);
    }
  };

  const handleSubmit = async (data: EmployeeFormData) => {
    if (!selectedLocation) {
      setFormError('Please select a location');
      return;
    }

    setFormError('');
    setSubmitting(true);

    try {
      // Convert NaN to undefined for number fields
      const monthlySalary = data.monthly_salary && !isNaN(data.monthly_salary) ? data.monthly_salary : undefined;
      const dailyWage = data.daily_wage && !isNaN(data.daily_wage) ? data.daily_wage : undefined;

      const employeeData = {
        location_id: selectedLocation.id,
        business_type: data.business_type,
        employee_code: data.employee_code,
        name: data.name,
        employee_type: data.employee_type,
        phone: data.phone || undefined,
        monthly_salary: monthlySalary,
        daily_wage: dailyWage,
        joining_date: data.joining_date,
      };

      if (editingEmployee) {
        // Update employee - Admin only
        if (user?.role !== 'admin') {
          setFormError('Only admins can edit employees');
          setSubmitting(false);
          return;
        }
        
        const updateData: any = {
          name: data.name,
          employee_type: data.employee_type,
          phone: data.phone || undefined,
        };
        
        if (data.employee_type === 'daily_wage_labour') {
          updateData.daily_wage = data.daily_wage;
          updateData.monthly_salary = undefined;
        } else {
          updateData.monthly_salary = data.monthly_salary;
          updateData.daily_wage = undefined;
        }

        await apiClient.put(`/employees/${editingEmployee.id}`, updateData);
      } else {
        // Create employee
        await apiClient.post('/employees', employeeData);
      }

      setShowForm(false);
      setEditingEmployee(null);
      form.reset();
      fetchEmployees();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to save employee');
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEmployee(null);
    setFormError('');
    form.reset();
  };

  const isAdmin = user?.role === 'admin';
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  if (!selectedLocation) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a location to view employees.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/payroll')}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          <span>Back to Payroll</span>
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
            <p className="text-gray-600 mt-1">Manage employee master data</p>
          </div>
          {isManagerOrAdmin && (
            <button
              onClick={() => {
                setEditingEmployee(null);
                setShowForm(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Employee</span>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-4">
            {employees.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                <p>No employees found. {isAdmin && 'Click "Add Employee" to create one.'}</p>
              </div>
            ) : (
              employees.map((emp) => {
                const formatDate = (dateString: string) => {
                  if (!dateString) return '-';
                  const date = new Date(dateString);
                  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
                };
                
                return (
                  <div key={emp.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{emp.name}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${emp.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {emp.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 font-mono">{emp.employee_code}</p>
                      </div>
                      {isAdmin && (
                        <div className="flex space-x-2 ml-2">
                          <button
                            onClick={() => handleToggleActive(emp)}
                            className={`p-1 ${emp.is_active ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`}
                            title={emp.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {emp.is_active ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <XCircleIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setEditingEmployee(emp);
                              setShowForm(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(emp.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type:</span>
                        <span className="text-gray-900 font-medium">
                          {emp.employee_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-900">{emp.phone || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Salary/Wage:</span>
                        <span className="text-gray-900 font-medium">
                          {emp.monthly_salary ? `₹${emp.monthly_salary.toLocaleString('en-IN')}/month` : emp.daily_wage ? `₹${emp.daily_wage.toLocaleString('en-IN')}/day` : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Joining Date:</span>
                        <span className="text-gray-900">{formatDate(emp.joining_date)}</span>
                      </div>
                      {emp.location_name && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Location:</span>
                          <span className="text-gray-900">{emp.location_name}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Business Type:</span>
                        <span className="text-gray-900 capitalize">{emp.business_type}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salary/Wage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joining Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((emp) => {
                    const formatDate = (dateString: string) => {
                      if (!dateString) return '-';
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
                    };
                    
                    return (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 font-mono">{emp.employee_code}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{emp.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {emp.employee_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{emp.phone || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {emp.monthly_salary ? `₹${emp.monthly_salary.toLocaleString('en-IN')}/month` : emp.daily_wage ? `₹${emp.daily_wage.toLocaleString('en-IN')}/day` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(emp.joining_date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${emp.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {emp.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-3">
                              <button
                                onClick={() => handleToggleActive(emp)}
                                className={`transition-colors ${emp.is_active ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`}
                                title={emp.is_active ? 'Deactivate Employee' : 'Activate Employee'}
                              >
                                {emp.is_active ? (
                                  <CheckCircleIcon className="h-5 w-5" />
                                ) : (
                                  <XCircleIcon className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingEmployee(emp);
                                  setShowForm(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title="Edit Employee"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(emp.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Delete Employee"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {employees.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No employees found. {isAdmin && 'Click "Add Employee" to create one.'}</p>
              </div>
            )}
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editingEmployee ? 'Edit Employee' : 'Add Employee'}
              </h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee Type - First Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('employee_type')}
                    disabled={!isAdmin && !editingEmployee}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.employee_type ? 'border-red-500' : 'border-gray-300'
                    } ${!isAdmin && !editingEmployee ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    {isAdmin ? (
                      <>
                        <option value="manager">Manager</option>
                        <option value="regular_farm_labour">Regular Farm Labour</option>
                        <option value="daily_wage_labour">Daily Wage Labour</option>
                      </>
                    ) : (
                      <option value="daily_wage_labour">Daily Wage Labour</option>
                    )}
                  </select>
                  {form.formState.errors.employee_type && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.employee_type.message}</p>
                  )}
                  {!isAdmin && !editingEmployee && (
                    <p className="mt-1 text-xs text-gray-500">Managers can only add daily wage labour employees</p>
                  )}
                </div>

                {/* Employee Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...form.register('employee_code')}
                    readOnly
                    disabled={generatingCode}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.employee_code ? 'border-red-500' : 'border-gray-300'
                    } bg-gray-100 cursor-not-allowed`}
                    placeholder={generatingCode ? "Generating..." : "Auto-generated"}
                  />
                  {form.formState.errors.employee_code && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.employee_code.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-generated ({employeeType === 'daily_wage_labour' ? 'DWL' : 'EMP'} prefix)
                  </p>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...form.register('name')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Employee name"
                  />
                  {form.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    {...form.register('phone')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="10-digit mobile number"
                  />
                  {form.formState.errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.phone.message}</p>
                  )}
                </div>

                {/* Monthly Salary (for Manager and Regular Farm Labour) */}
                {(employeeType === 'manager' || employeeType === 'regular_farm_labour') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Salary (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...form.register('monthly_salary', { 
                        valueAsNumber: true,
                        setValueAs: (v) => v === '' || isNaN(Number(v)) ? undefined : Number(v)
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        form.formState.errors.monthly_salary ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 25000"
                    />
                    {form.formState.errors.monthly_salary && (
                      <p className="mt-1 text-sm text-red-600">{form.formState.errors.monthly_salary.message}</p>
                    )}
                  </div>
                )}

                {/* Daily Wage (for Daily Wage Labour) */}
                {employeeType === 'daily_wage_labour' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Daily Wage (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...form.register('daily_wage', { 
                        valueAsNumber: true,
                        setValueAs: (v) => v === '' || isNaN(Number(v)) ? undefined : Number(v)
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        form.formState.errors.daily_wage ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 350"
                    />
                    {form.formState.errors.daily_wage && (
                      <p className="mt-1 text-sm text-red-600">{form.formState.errors.daily_wage.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Default rate. Can be overridden per attendance or salary period.</p>
                  </div>
                )}

                {/* Business Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('business_type')}
                    disabled={!!editingEmployee}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.business_type ? 'border-red-500' : 'border-gray-300'
                    } ${editingEmployee ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="fish">Fish</option>
                    <option value="goat">Goat</option>
                  </select>
                  {form.formState.errors.business_type && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.business_type.message}</p>
                  )}
                  {editingEmployee && (
                    <p className="mt-1 text-xs text-gray-500">Business type cannot be changed after creation</p>
                  )}
                </div>

                {/* Joining Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Joining Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...form.register('joining_date')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.joining_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {form.formState.errors.joining_date && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.joining_date.message}</p>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>{editingEmployee ? 'Update Employee' : 'Create Employee'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;

